import { UI_LANGUAGES } from "@/i18n/languages";
import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useLanguage,
  type LanguageCode,
} from "@/store/use-ui-preferences-store";
import { useTranslation } from "react-i18next";

interface Language {
  code: LanguageCode;
  name: string;
  flag: string;
}

export const LanguageSelector = () => {
  const { t } = useTranslation();
  const { language, draftLanguage, setDraftLanguage, isUpdatingLanguage } =
    useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Currently supported languages in UI (subset of SUPPORTED_LANGUAGES)
  const languages: Language[] = UI_LANGUAGES.map(({ locale, code, flag }) => ({
    code, flag, name: t(`language.${locale}`),
  }));

  // Use draft value if available, otherwise current language
  const displayLanguage = draftLanguage || language;
  const currentLanguage =
    languages.find((lang) => lang.code === displayLanguage) || languages[0];

  const handleLanguageChange = (languageCode: LanguageCode) => {
    setDraftLanguage(languageCode); // Draft'a kaydet
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isUpdatingLanguage}
        className="w-full max-w-[240px] px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-border-primary dark:border-border-primary flex items-center justify-between text-base font-medium text-text-primary dark:text-text-primary hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm disabled:opacity-50"
      >
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs">
            {currentLanguage.flag}
          </div>
          <span>{currentLanguage.name}</span>
        </div>
        <ChevronDown
          className={cn(
            "w-4 h-4 text-text-quaternary dark:text-text-quaternary transition-transform duration-200",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 w-full max-w-[240px] mt-1 bg-white dark:bg-gray-800 border border-border-primary dark:border-border-primary rounded-lg shadow-lg z-50 overflow-hidden">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() =>
                handleLanguageChange(language.code as LanguageCode)
              }
              className={cn(
                "w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors",
                language.code === currentLanguage.code &&
                  "bg-gray-50 dark:bg-gray-700"
              )}
            >
              <div className="w-5 h-5 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs">
                {language.flag}
              </div>
              <span className="text-base font-medium text-text-primary dark:text-text-primary">
                {language.name}
              </span>
              {language.code === currentLanguage.code && (
                <div className="ml-auto w-2 h-2 rounded-full bg-blue-500" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
